import React from 'react';
import { Accordion, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faChevronUp, faChevronDown, faCopy, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ScheduleCard from './ScheduleCard';
import type { ScheduleUI } from '../types';

interface Props {
  schedules: ScheduleUI[];
  setSchedules: (s: ScheduleUI[]) => void;
  zones: any[];
  schedulingWindows: any[];
  windowTimezone: string;
  forbiddenScheduleDependencyWindowIds?: Set<number>;
}

const emptySchedule = (): ScheduleUI => ({
  scheduleName: '',
  // time
  start_time: undefined,
  end_time: undefined,

  // absolute date bounds
  date_from: undefined,
  date_until: undefined,

  // month range (1..12)
  month_from: undefined,
  month_to: undefined,

  // week-of-month range (1..5)
  wom_from: undefined,
  wom_to: undefined,

  // day-of-week range (1..7)
  dow_from: undefined,
  dow_to: undefined,

  // nth-of-month
  nth_of_month_from: undefined,
  nth_of_month_until: undefined,

  // closed-day shift
  shift_if_closed_window_path: undefined,

  shift_open_days: undefined,

  nth_of_month_from_in_scheduling_window_path: undefined,
  nth_of_month_until_in_scheduling_window_path: undefined,
  open_during_scheduling_window_path: undefined,
  closed_during_scheduling_window_path: undefined,
});

const SchedulesSection: React.FC<Props> = ({ schedules, setSchedules, zones, schedulingWindows, windowTimezone, forbiddenScheduleDependencyWindowIds }) => {
  const { t } = useTranslation();

  const upsert = (i: number, patch: Partial<ScheduleUI>) => {
    const next = [...schedules];
    next[i] = { ...next[i], ...patch };
    setSchedules(next);
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= schedules.length) return;
    const next = [...schedules];
    [next[i], next[j]] = [next[j], next[i]];
    setSchedules(next);
  };

  const duplicate = (i: number) => {
    const next = [...schedules];
    next.splice(i + 1, 0, {
      ...next[i],
      scheduleName: `${next[i].scheduleName || t('Copy')} (dup)`,
    });
    setSchedules(next);
  };

  const remove = (i: number) => {
    setSchedules(schedules.filter((_, idx) => idx !== i));
  };

  return (
    <Accordion defaultActiveKey="1" className="mb-3">
      <Accordion.Item eventKey="1">
        <Accordion.Header>{t('Schedules')}</Accordion.Header>
        <Accordion.Body>
          <div className="mb-2">
            <Button className="me-2" onClick={() => setSchedules([...schedules, emptySchedule()])}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              {t('Add Schedule')}
            </Button>
          </div>

          {schedules.map((s, idx) => (
            <ScheduleCard
              key={idx}
              schedule={s}
              onChange={(patch) => upsert(idx, patch)}
              actions={[
                {
                  icon: faCopy,
                  title: t('Duplicate') as string,
                  onClick: () => duplicate(idx),
                },
                {
                  icon: faChevronUp,
                  title: t('Move up') as string,
                  onClick: () => move(idx, -1),
                },
                {
                  icon: faChevronDown,
                  title: t('Move down') as string,
                  onClick: () => move(idx, +1),
                },
                {
                  icon: faTrash,
                  title: t('Delete') as string,
                  onClick: () => remove(idx),
                  variant: 'danger' as const,
                },
              ]}
              zones={zones}
              schedulingWindows={schedulingWindows}
              forbiddenScheduleDependencyWindowIds={forbiddenScheduleDependencyWindowIds}
            />
          ))}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SchedulesSection;
